import {
  selfDisclosureContractLabels,
  selfDisclosureContracts,
  selfDisclosureEmploymentLabels,
  selfDisclosureEmployments,
  selfDisclosureHousingSituationLabels,
  selfDisclosureHousingSituations,
  selfDisclosureMaritalStatusLabels,
  selfDisclosureMaritalStatuses,
  selfDisclosureOtherIncomeTypeLabels,
  selfDisclosureOtherIncomeTypes,
  selfDisclosureSalariesPerYear,
} from '@brimo/shared';
import { Select, TextField } from '../../../ds/components';
import { EuroField, InlineMonthInput, MonthField, RadioCardsField, SegmentedField } from './fields';
import type { AssetsForm, ExpensesForm, FieldErrors, IncomeForm, PersonForm } from './form';
import styles from './SelfDisclosurePublicPage.module.css';

interface StepProps<T> {
  form: T;
  errors: FieldErrors;
  onChange: (patch: Partial<T>) => void;
  /** A field lost focus — the flow saves the step when it is valid and changed. */
  onBlur: () => void;
}

/** Step 1 „Person" — derived from the summary screens (the form has no frame of its own). */
export function PersonStep({ form, errors, onChange, onBlur }: StepProps<PersonForm>) {
  return (
    <div className={styles.formGrid} onBlur={onBlur}>
      <TextField
        label="Vorname"
        required
        value={form.firstName}
        error={errors['firstName']}
        autoComplete="given-name"
        className={styles.field}
        onChange={(event) => onChange({ firstName: event.target.value })}
      />
      <TextField
        label="Nachname"
        required
        value={form.lastName}
        error={errors['lastName']}
        autoComplete="family-name"
        className={styles.field}
        onChange={(event) => onChange({ lastName: event.target.value })}
      />
      <TextField
        label="Geburtsdatum"
        type="date"
        value={form.bornOn}
        error={errors['bornOn']}
        autoComplete="bday"
        className={styles.field}
        onChange={(event) => onChange({ bornOn: event.target.value })}
      />
      <Select
        label="Familienstand"
        value={form.maritalStatus}
        error={errors['maritalStatus']}
        placeholder="Bitte wählen"
        className={styles.field}
        options={selfDisclosureMaritalStatuses.map((value) => ({
          value,
          label: selfDisclosureMaritalStatusLabels[value],
        }))}
        onChange={(event) => onChange({ maritalStatus: event.target.value })}
      />
      <TextField
        label="Kinder"
        type="number"
        min={0}
        max={20}
        value={form.children}
        error={errors['children']}
        className={styles.field}
        onChange={(event) => onChange({ children: event.target.value })}
      />
      <TextField
        label="Staatsangehörigkeit"
        value={form.nationality}
        error={errors['nationality']}
        autoComplete="country-name"
        className={styles.field}
        onChange={(event) => onChange({ nationality: event.target.value })}
      />
      <TextField
        label="Straße und Hausnummer"
        value={form.street}
        error={errors['street']}
        autoComplete="street-address"
        className={`${styles.field} ${styles.fieldWide}`}
        onChange={(event) => onChange({ street: event.target.value })}
      />
      <TextField
        label="PLZ"
        value={form.postalCode}
        error={errors['postalCode']}
        inputMode="numeric"
        autoComplete="postal-code"
        className={styles.field}
        onChange={(event) => onChange({ postalCode: event.target.value })}
      />
      <TextField
        label="Ort"
        value={form.city}
        error={errors['city']}
        autoComplete="address-level2"
        className={styles.field}
        onChange={(event) => onChange({ city: event.target.value })}
      />
      <TextField
        label="Telefon"
        type="tel"
        value={form.phone}
        error={errors['phone']}
        autoComplete="tel"
        className={styles.field}
        onChange={(event) => onChange({ phone: event.target.value })}
      />
      <TextField
        label="E-Mail"
        type="email"
        value={form.email}
        error={errors['email']}
        autoComplete="email"
        className={styles.field}
        onChange={(event) => onChange({ email: event.target.value })}
      />
    </div>
  );
}

const employed = (employment: IncomeForm['employment']) =>
  employment === 'employed' || employment === 'civil_servant';

/** Step 2 „Beschäftigung und Einkommen" (Selbstauskunft 03). */
export function IncomeStep({ form, errors, onChange, onBlur }: StepProps<IncomeForm>) {
  return (
    <div className={styles.formGrid} onBlur={onBlur}>
      <SegmentedField
        label="Beschäftigung"
        required
        options={selfDisclosureEmployments.map((value) => ({
          value,
          label: selfDisclosureEmploymentLabels[value],
        }))}
        value={form.employment}
        error={errors['employment']}
        onChange={(employment) => {
          onChange({ employment });
          onBlur();
        }}
      />
      {employed(form.employment) ? (
        <TextField
          label="Arbeitgeber"
          required
          value={form.employer}
          error={errors['employer']}
          autoComplete="organization"
          className={styles.field}
          onChange={(event) => onChange({ employer: event.target.value })}
        />
      ) : null}
      {form.employment !== 'retired' ? (
        <MonthField
          label={form.employment === 'self_employed' ? 'Selbstständig seit' : 'Beschäftigt seit'}
          required
          value={form.employedSince}
          error={errors['employedSinceOn']}
          onChange={(employedSince) => onChange({ employedSince })}
        />
      ) : null}
      {employed(form.employment) ? (
        <RadioCardsField
          label="Arbeitsvertrag"
          name="contract"
          required
          value={form.contract}
          error={errors['contract'] ?? errors['fixedTermUntilOn']}
          options={selfDisclosureContracts.map((value) => ({
            value,
            label: selfDisclosureContractLabels[value],
            // the „bis MM/JJJJ" line of the frame becomes the input of the fixed term once selected
            description:
              value === 'fixed_term' && form.contract === 'fixed_term' ? (
                <InlineMonthInput
                  label="Befristet bis"
                  value={form.fixedTermUntil}
                  invalid={Boolean(errors['fixedTermUntilOn'])}
                  onChange={(fixedTermUntil) => onChange({ fixedTermUntil })}
                />
              ) : undefined,
          }))}
          onChange={(contract) => {
            onChange({ contract });
            onBlur();
          }}
        />
      ) : null}
      <EuroField
        label="Nettoeinkommen"
        required
        value={form.netIncome}
        error={errors['netIncomeCents']}
        hint="Durchschnitt der letzten 3 Monate, ohne Sonderzahlungen."
        onChange={(netIncome) => onChange({ netIncome })}
      />
      <Select
        label="Gehälter pro Jahr"
        value={form.salariesPerYear}
        error={errors['salariesPerYear']}
        placeholder="Bitte wählen"
        className={styles.field}
        options={selfDisclosureSalariesPerYear.map((value) => ({
          value: String(value),
          label: String(value),
        }))}
        onChange={(event) => {
          onChange({ salariesPerYear: event.target.value });
          onBlur();
        }}
      />
      <EuroField
        label="Weitere Einnahmen"
        value={form.otherIncome}
        error={errors['otherIncomeCents']}
        hint="z. B. Nebentätigkeit, Kindergeld, bestehende Mieteinnahmen"
        onChange={(otherIncome) => onChange({ otherIncome })}
      />
      <Select
        label="Art der weiteren Einnahmen"
        value={form.otherIncomeType}
        error={errors['otherIncomeType']}
        placeholder="Bitte wählen"
        className={styles.field}
        options={selfDisclosureOtherIncomeTypes.map((value) => ({
          value,
          label: selfDisclosureOtherIncomeTypeLabels[value],
        }))}
        onChange={(event) => {
          onChange({ otherIncomeType: event.target.value });
          onBlur();
        }}
      />
      <EuroField
        label="Sonderzahlungen pro Jahr"
        value={form.specialPayments}
        error={errors['specialPaymentsPaCents']}
        hint="Weihnachts- und Urlaubsgeld, Boni"
        onChange={(specialPayments) => onChange({ specialPayments })}
      />
      <EuroField
        label="Bestehende Mieteinnahmen"
        value={form.rentalIncome}
        error={errors['rentalIncomeCents']}
        hint="monatlich, aus vermieteten Objekten"
        onChange={(rentalIncome) => onChange({ rentalIncome })}
      />
    </div>
  );
}

/** Step 3 „Monatliche Ausgaben" (Selbstauskunft 04). */
export function ExpensesStep({ form, errors, onChange, onBlur }: StepProps<ExpensesForm>) {
  const renting = form.housingSituation === 'rent';
  return (
    <div className={styles.formGrid} onBlur={onBlur}>
      <SegmentedField
        label="Aktuelle Wohnsituation"
        required
        options={selfDisclosureHousingSituations.map((value) => ({
          value,
          label: selfDisclosureHousingSituationLabels[value],
        }))}
        value={form.housingSituation}
        error={errors['housingSituation']}
        onChange={(housingSituation) => {
          onChange({ housingSituation });
          onBlur();
        }}
      />
      <EuroField
        label="Warmmiete"
        required={renting}
        value={form.grossRent}
        error={errors['grossRentCents']}
        onChange={(grossRent) => onChange({ grossRent })}
      />
      <Select
        label="Bleibt die Miete nach dem Kauf?"
        value={form.rentContinues}
        className={styles.field}
        options={[
          { value: 'yes', label: 'Ja, ich ziehe nicht ein' },
          { value: 'no', label: 'Nein, ich ziehe selbst ein' },
        ]}
        onChange={(event) => {
          onChange({ rentContinues: event.target.value === 'no' ? 'no' : 'yes' });
          onBlur();
        }}
      />
      <EuroField
        label="Leasing und Ratenkredite"
        value={form.loans}
        error={errors['loansCents']}
        onChange={(loans) => onChange({ loans })}
      />
      <MonthField
        label="Restlaufzeit"
        value={form.loansRemainingTerm}
        error={errors['loansRemainingTerm']}
        onChange={(loansRemainingTerm) => onChange({ loansRemainingTerm })}
      />
      <EuroField
        label="Unterhaltszahlungen"
        value={form.alimony}
        error={errors['alimonyCents']}
        onChange={(alimony) => onChange({ alimony })}
      />
      <EuroField
        label="Private Krankenversicherung"
        value={form.privateHealthInsurance}
        error={errors['privateHealthInsuranceCents']}
        onChange={(privateHealthInsurance) => onChange({ privateHealthInsurance })}
      />
    </div>
  );
}

/** Step 4 „Vermögen" (summary rows of Selbstauskunft 05/08; the proofs follow below the fields). */
export function AssetsStep({ form, errors, onChange, onBlur }: StepProps<AssetsForm>) {
  return (
    <div className={styles.formGrid} onBlur={onBlur}>
      <EuroField
        label="Eigenkapital"
        value={form.equity}
        error={errors['equityCents']}
        hint="Guthaben, das Sie für den Kauf einsetzen können"
        onChange={(equity) => onChange({ equity })}
      />
      <EuroField
        label="Wertpapiere"
        value={form.securities}
        error={errors['securitiesCents']}
        onChange={(securities) => onChange({ securities })}
      />
      <EuroField
        label="Sonstiges Vermögen"
        value={form.other}
        error={errors['otherCents']}
        hint="z. B. Bausparverträge, Lebensversicherungen"
        onChange={(other) => onChange({ other })}
      />
    </div>
  );
}
